/**
 * Purpose: Primary visitor conversion actions — Call, WhatsApp, Directions, Share.
 *          (Save/favorite ships with the Phase 6 favorites backend.)
 * Inputs: business.
 * Outputs: horizontally scrollable action row; each action renders only when
 *          its target exists (no dead buttons).
 */
import React from 'react';
import { CTAButton } from '../../ui';
import { useToast } from '../../../contexts/ToastContext';
import { getBusinessContacts, contactsOfKind, primaryWhatsAppUrl } from '../../../utils/contacts';
import { hasGoogleMapsTarget, resolveGoogleMapsHref } from '../../../utils/googleMaps';

const Icon = ({ d }) => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
  </svg>
);

const PHONE_D =
  'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z';
const CHAT_D =
  'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z';
const PIN_D =
  'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z';
const SHARE_D =
  'M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z';

const VisitorActionBar = ({ business }) => {
  const { showSuccess, showError } = useToast();
  const contacts = getBusinessContacts(business);
  const phone = contactsOfKind(contacts, 'phone')[0]?.value || null;
  const whatsAppHref = primaryWhatsAppUrl(contacts);
  const mapsTarget = {
    latitude: business.latitude,
    longitude: business.longitude,
    address: business.address,
    city: business.city,
    name: business.name,
  };
  const directionsHref = hasGoogleMapsTarget(mapsTarget) ? resolveGoogleMapsHref(mapsTarget) : null;

  const handleShare = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const shareData = { title: business.name, text: business.name, url };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
      await navigator.clipboard.writeText(url);
      showSuccess('Link copied to clipboard');
    } catch (err) {
      if (err?.name === 'AbortError') return; // user closed the share sheet
      showError('Could not share this page');
    }
  };

  return (
    <div
      className="scrollbar-hide flex items-center gap-2 overflow-x-auto"
      role="group"
      aria-label="Business actions"
    >
      {phone && (
        <CTAButton href={`tel:${phone}`} icon={<Icon d={PHONE_D} />} aria-label={`Call ${business.name}`}>
          Call
        </CTAButton>
      )}
      {whatsAppHref && (
        <CTAButton
          href={whatsAppHref}
          target="_blank"
          rel="noopener noreferrer"
          variant="secondary"
          icon={<Icon d={CHAT_D} />}
          aria-label={`WhatsApp ${business.name}`}
        >
          WhatsApp
        </CTAButton>
      )}
      {directionsHref && (
        <CTAButton
          href={directionsHref}
          target="_blank"
          rel="noopener noreferrer"
          variant="secondary"
          icon={<Icon d={PIN_D} />}
          aria-label={`Directions to ${business.name}`}
        >
          Directions
        </CTAButton>
      )}
      <CTAButton variant="secondary" onClick={handleShare} icon={<Icon d={SHARE_D} />} aria-label="Share this business">
        Share
      </CTAButton>
    </div>
  );
};

export default VisitorActionBar;
